/**
 * deriveAppView — the single source of truth for "which surface does this
 * `/app*` pathname render?", used by WorkspaceContainer to switch the content
 * column WITHOUT unmounting the app shell (the wordmark top bar + NavTray).
 *
 * Shell-persistence fix (2026-07-16): before this, Feed / Cooks (ledger) /
 * Groups / Custom logic / Syntax help were STANDALONE top-level route objects
 * (`routes/feed.tsx` etc.) whose element was NOT `<AppRoute/>` — so navigating
 * to any of them unmounted the whole shell (NavTray + top bar), forcing the user
 * to go "back" to `/app` before reaching another destination. Registering those
 * paths as CHILDREN of the `/app` route object (entry.client.tsx) keeps the
 * single `<AppRoute/>` instance mounted, and this pure helper tells the shell
 * which view to render in its content column — exactly the mechanism the
 * `/app` ↔ `/app/library` split already used (design.md §6.0a: a labelled
 * primary-nav entry stays reachable on every surface; branding.md §5.2.1: no
 * banned words).
 *
 * Pure + exported so the mapping is unit-testable without a router.
 */

/**
 * The surface a `/app*` pathname resolves to. `dashboard`, `library`, and
 * `recipe` are the "primary" surfaces the shell already owned; the rest are the
 * secondary tray destinations this fix folds into the same shell.
 */
export type AppView =
  | { kind: "dashboard" }
  | { kind: "library" }
  | { kind: "feed" }
  | { kind: "cooks" }
  | { kind: "groups" }
  | { kind: "group"; groupUuid: string }
  | { kind: "group-share"; groupUuid: string; shareUuid: string }
  | { kind: "discover" }
  | { kind: "custom-logic" }
  | { kind: "help" }
  | { kind: "recipe" };

/**
 * The tray destinations that render in the secondary content surface (as
 * opposed to the dashboard/library shell or the recipe Workspace). Kept in sync
 * with `isSecondaryAppView` below.
 */
export type SecondaryAppViewKind =
  "feed" | "cooks" | "groups" | "group" | "group-share" | "discover" | "custom-logic" | "help";

/**
 * Decode a `/app/g/:groupUuid(/r/:shareUuid)` pathname into its uuids. Returns
 * null for a bare `/app/g` (the Groups index). Tolerates a trailing slash and
 * URL-encoded segments. Kept local (not exported) — `deriveAppView` is the
 * public surface.
 */
function decodeGroupPath(pathname: string): AppView | null {
  // Normalise: strip a single trailing slash so "/app/g/" reads as "/app/g".
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (path === "/app/g") return { kind: "groups" };
  // /app/g/<groupUuid>/r/<shareUuid>
  const shareMatch = path.match(/^\/app\/g\/([^/]+)\/r\/([^/]+)$/);
  if (shareMatch) {
    const groupUuid = safeDecode(shareMatch[1] ?? "");
    const shareUuid = safeDecode(shareMatch[2] ?? "");
    if (groupUuid && shareUuid) return { kind: "group-share", groupUuid, shareUuid };
    return { kind: "groups" };
  }
  // /app/g/<groupUuid>
  const groupMatch = path.match(/^\/app\/g\/([^/]+)$/);
  if (groupMatch) {
    const groupUuid = safeDecode(groupMatch[1] ?? "");
    if (groupUuid) return { kind: "group", groupUuid };
    return { kind: "groups" };
  }
  // Any deeper/unknown /app/g/* path falls back to the Groups index rather than
  // dropping the shell (never a NotFound for an authed group path).
  if (path.startsWith("/app/g/") || path.startsWith("/app/g")) return { kind: "groups" };
  return null;
}

/** decodeURIComponent that never throws on a malformed sequence. */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Map a pathname to the app surface it renders. Order matters: the more specific
 * `/app/settings/custom-logic` is tested before the `/app/g`, `/app/r`, and
 * bare-`/app` branches. Anything that is not a recognised secondary destination
 * resolves to `dashboard` / `library` / `recipe` (the primary surfaces the shell
 * always owned), so an unknown `/app/*` path still renders inside the shell
 * rather than unmounting it.
 */
export function deriveAppView(pathname: string): AppView {
  // Recipe surface (Cook / Editor) — a separate top-level route object, kept as
  // "recipe" so the shell shows the Workspace, not a secondary destination.
  if (pathname === "/app/r" || pathname.startsWith("/app/r/")) return { kind: "recipe" };

  // Secondary tray destinations.
  if (pathname === "/app/feed" || pathname.startsWith("/app/feed/")) return { kind: "feed" };
  if (pathname === "/app/ledger" || pathname.startsWith("/app/ledger/")) return { kind: "cooks" };
  if (pathname === "/app/discover" || pathname.startsWith("/app/discover/")) {
    return { kind: "discover" };
  }
  if (pathname === "/app/settings/custom-logic") return { kind: "custom-logic" };
  if (pathname === "/app/help" || pathname.startsWith("/app/help/")) return { kind: "help" };
  const group = decodeGroupPath(pathname);
  if (group) return group;

  // Primary shell surfaces.
  if (pathname === "/app/library" || pathname.startsWith("/app/library/")) {
    return { kind: "library" };
  }
  // Bare `/app` (and any other unmatched `/app*`) is the dashboard HOME.
  return { kind: "dashboard" };
}

/**
 * True when the view renders in the secondary content surface (Feed / Cooks /
 * Groups / group detail / group share / Custom logic / Syntax help) rather than
 * the dashboard/library shell or the recipe Workspace.
 */
export function isSecondaryAppView(
  view: AppView,
): view is Extract<AppView, { kind: SecondaryAppViewKind }> {
  switch (view.kind) {
    case "feed":
    case "cooks":
    case "groups":
    case "group":
    case "group-share":
    case "discover":
    case "custom-logic":
    case "help":
      return true;
    default:
      return false;
  }
}
